import { useState, useEffect } from "react";
import { Zap, Mail, KeyRound, Loader2, Plus, Ticket, HelpCircle, ShieldCheck, ArrowRight } from "lucide-react";
import api, { setToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";
import RegisterDialog from "./RegisterDialog";
import RedeemDialog from "./RedeemDialog";
import AccessRequestDialog from "./AccessRequestDialog";
import SetupAdminDialog from "./SetupAdminDialog";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
  </svg>
);

export default function LoginLayer({ onSuccess, onBack }) {
  const [mode, setMode] = useState("choose"); // choose | returning
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [setupAdminOpen, setSetupAdminOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("invite")) setRedeemOpen(true);
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !pwd) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/user-login", {
        email: email.trim().toLowerCase(),
        password: pwd,
      });
      setToken(res.data.token);
      setStoredUser(res.data.user);
      toast.success(`Benvenuto/a ${res.data.user.full_name}!`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email o password errati");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-layer" className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-[#FBF9F6] overflow-y-auto">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }} />
      <div className="absolute top-[-120px] right-[-120px] h-[420px] w-[420px] rounded-full bg-[#FF7C43]/15 blur-3xl" />
      <div className="absolute bottom-[-160px] left-[-100px] h-[380px] w-[380px] rounded-full bg-[#C87941]/15 blur-3xl" />

      <div className="relative w-full max-w-md animate-fade my-8">
        {onBack && (
          <button
            type="button"
            data-testid="login-back-to-welcome"
            onClick={onBack}
            className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-[#C87941] hover:underline"
          >
            ← Torna alla home
          </button>
        )}
        {/* HEADER (always shown) */}
        <div className="mb-6 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#E8E1D9] bg-white/70 px-4 py-1.5 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF7C43] pulse-dot" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">Cuor di Papaya</span>
          </div>
          <div className="mb-5 inline-flex h-20 w-20 items-center justify-center float-slow">
            <img src="/logo-zap.png" alt="EASY Family logo" className="h-20 w-20 object-contain drop-shadow-lg" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#2D2926]">
            EASY <span className="italic font-light text-[#FF7C43]">Family</span>
          </h1>
          <p className="mt-2 font-display text-xl sm:text-2xl italic font-medium text-[#C87941]">
            Facciamo uno <span className="not-italic font-black text-[#FF7C43]">ZAP?</span>
          </p>
        </div>

        {/* === MODE: CHOOSE (default for first visit / unsure users) === */}
        {mode === "choose" && (
          <div className="space-y-3">
            {/* ELEVATOR PITCH — what is a ZAP */}
            <div className="rounded-3xl border border-[#FF7C43]/25 bg-gradient-to-br from-white to-[#FFF4EC] p-5 shadow-sm">
              <p className="text-center text-sm leading-relaxed text-[#2D2926]">
                <strong className="text-[#FF7C43]">EASY Family</strong> è come <strong>Booking.com</strong>, ma la "camera doppia" è la <strong>disponibilità dei tuoi cari</strong>.
              </p>
              <p className="mt-2 text-center text-xs leading-relaxed text-[#5C5855]">
                Ogni familiare collega il proprio <strong>Google Calendar</strong>. L'app incrocia i calendari e ti dice <strong>quando siete tutti liberi</strong> — zero vocali, zero votazioni.
              </p>
              <div className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#FF7C43]/10 px-3 py-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  ⚡ Un click = uno ZAP = ci vediamo
                </span>
              </div>
            </div>

            <div className="rounded-3xl border border-[#E8E1D9] bg-white/80 p-5 shadow-sm backdrop-blur-xl">
              <p className="text-center text-base font-semibold text-[#2D2926]">
                Ciao! Sei nuovo o stai tornando? 👋
              </p>
              <p className="mt-1 text-center text-sm text-[#5C5855]">
                Scegli l'opzione che fa per te:
              </p>
            </div>

            {/* Returning user */}
            <button
              data-testid="mode-returning"
              type="button"
              onClick={() => setMode("returning")}
              className="group flex w-full items-center gap-4 rounded-2xl border border-[#E8E1D9] bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FF7C43]/10 text-[#FF7C43]">
                <KeyRound className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D2926]">Ho già il mio account</p>
                <p className="text-xs text-[#5C5855]">Entro con email e password che ho creato</p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#C87941] transition group-hover:translate-x-1" />
            </button>

            {/* First-time admin */}
            <button
              data-testid="mode-setup-admin"
              type="button"
              onClick={() => setSetupAdminOpen(true)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-[#E8E1D9] bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D2926]">Sono il capofamiglia (prima volta)</p>
                <p className="text-xs text-[#5C5855]">Ho il <strong>codice della Casa</strong> e creo il mio account personale</p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#C87941] transition group-hover:translate-x-1" />
            </button>

            {/* Invite code */}
            <button
              data-testid="mode-redeem"
              type="button"
              onClick={() => setRedeemOpen(true)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-[#E8E1D9] bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FF7C43]/10 text-[#FF7C43]">
                <Ticket className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D2926]">Ho ricevuto un invito</p>
                <p className="text-xs text-[#5C5855]">Mi è arrivato un <strong>codice ZAP-XXXXX</strong> via WhatsApp</p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#C87941] transition group-hover:translate-x-1" />
            </button>

            {/* Google login */}
            <button
              data-testid="google-login-button"
              type="button"
              onClick={googleLogin}
              className="group flex w-full items-center gap-4 rounded-2xl border border-[#E8E1D9] bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white border border-[#E8E1D9]">
                <GoogleIcon />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D2926]">Entro con Google</p>
                <p className="text-xs text-[#5C5855]">Uso il mio account Gmail</p>
              </div>
              <ArrowRight className="h-4 w-4 text-[#C87941] transition group-hover:translate-x-1" />
            </button>

            <div className="pt-2 flex flex-col gap-2">
              <button
                data-testid="open-register-button"
                type="button"
                onClick={() => setRegisterOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[#C87941]/40 bg-white/60 py-2.5 text-xs font-semibold text-[#C87941] transition hover:border-[#C87941] hover:bg-white"
              >
                <Plus className="h-3.5 w-3.5" />
                Creo una Casa nuova
              </button>
              <button
                data-testid="ask-access-button"
                type="button"
                onClick={() => setAccessOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[#FF7C43]/40 bg-white/60 py-2.5 text-xs font-semibold text-[#FF7C43] transition hover:border-[#FF7C43] hover:bg-white"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                Non so cosa fare — chiedo aiuto
              </button>
            </div>
          </div>
        )}

        {/* === MODE: RETURNING — email + password form === */}
        {mode === "returning" && (
          <form onSubmit={submit}>
            <button
              type="button"
              onClick={() => setMode("choose")}
              className="mb-4 text-xs font-semibold text-[#C87941] hover:underline"
            >
              ← Torna indietro
            </button>

            <div className="rounded-3xl border border-[#E8E1D9] bg-white/80 p-6 shadow-sm backdrop-blur-xl">
              <p className="mb-4 text-sm text-[#5C5855]">
                Bentornato/a! Inserisci l'<strong className="text-[#2D2926]">email personale</strong> e la <strong className="text-[#2D2926]">password</strong> che hai creato la prima volta.
              </p>

              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">La tua email</label>
              <div className="relative mb-1">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#C87941]" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="es. mario.rossi@gmail.com"
                  autoComplete="email"
                  autoFocus
                  className="w-full rounded-2xl border border-[#E8E1D9] bg-white py-3.5 pl-12 pr-4 text-base font-medium text-[#2D2926] outline-none transition focus:border-[#FF7C43] focus:ring-4 focus:ring-[#FF7C43]/15"
                />
              </div>
              <p className="mb-3 text-[11px] text-[#5C5855]/80">L'email che hai scelto al primo accesso (non quella della famiglia)</p>

              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">La tua password</label>
              <div className="relative mb-1">
                <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#C87941]" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full rounded-2xl border border-[#E8E1D9] bg-white py-3.5 pl-12 pr-4 text-base font-medium text-[#2D2926] outline-none transition focus:border-[#FF7C43] focus:ring-4 focus:ring-[#FF7C43]/15"
                />
              </div>
              <p className="mb-3 text-[11px] text-[#5C5855]/80">La password personale che hai scelto (almeno 8 caratteri)</p>

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading || !email || !pwd}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl papaya-gradient py-4 text-base font-bold uppercase tracking-wider text-white shadow-lg transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <><Zap className="h-5 w-5" fill="white" /> Entra in Casa</>
                )}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-[#5C5855]">
              Non sei mai entrato/a prima?{" "}
              <button
                type="button"
                onClick={() => setMode("choose")}
                className="font-bold text-[#FF7C43] hover:underline"
              >
                Scegli un altro modo
              </button>
            </p>
          </form>
        )}

        <p className="mt-8 text-center text-[10px] font-bold uppercase tracking-[0.3em] text-[#C87941]/60">
          Fatto con amore da ZapHQ.space
        </p>
      </div>

      <RegisterDialog open={registerOpen} onClose={() => setRegisterOpen(false)} onRegistered={() => { setRegisterOpen(false); onSuccess(); }} />
      <RedeemDialog open={redeemOpen} onClose={() => setRedeemOpen(false)} onRedeemed={() => { setRedeemOpen(false); onSuccess(); }} />
      <AccessRequestDialog open={accessOpen} onClose={() => setAccessOpen(false)} />
      <SetupAdminDialog open={setupAdminOpen} onClose={() => setSetupAdminOpen(false)} onSuccess={() => { setSetupAdminOpen(false); onSuccess(); }} />
    </div>
  );
}
